import {
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  LoaderIcon,
  ScreenEmptyVideoIcon,
  ScreenProcessingIcon,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';

import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { TPresenceData } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { BoxRow } from '@/features/broadcast-schedule-program/types';
import { TRect } from '@/types';
import { CustomControlsWidget } from 'src/components/CustomControls/CustomControlsWidget';

import { VideoBoxes } from '../VideoBoxes/VideoBoxes';
import { VideoCanvas } from '../VideoCanvas/VideoCanvas';

import { useVideoKeyboardHandlers } from './hooks/useVideoKeyboardHandlers';
import { useVideoPlayerHandlers, VideoPlayerMainHandlers } from './hooks/useVideoPlayerHandlers';
import { useVideoPlayerState } from './hooks/useVideoPlayerState';

export type OnProgressType = {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
};

export type VideoProps = {
  url: string;
  playing?: boolean;
  muted?: boolean;
  activeBox?: BoxRow | null;
  presenceData?: TPresenceData;
  controls?: boolean;
  volume?: number;
  playbackRate?: number;
  width?: string;
  height?: string;
  customControls?: boolean;
  drawerLogo?: boolean;
  pendingSeekTime?: number | null;
  isLoading?: boolean;
  hasError?: boolean;
  className?: string;
  markers?: { pos: number; type: 'start' | 'end' }[];
  setPendingSeekTime?: React.Dispatch<React.SetStateAction<number | null>>;
  onDrawnRect?: (rect: TRect) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onProgress?: (onProgress: OnProgressType) => void;
  onEnded?: () => void;
  onEndPresence?: (handlers: VideoPlayerMainHandlers) => void;
  checkProgress?: (playedSeconds: number, handlers: VideoPlayerMainHandlers) => void;
  checkSeek?: (seekedSeconds: number) => void;
  setActiveBox?: React.Dispatch<React.SetStateAction<BoxRow | null>>;
  setPlayerVideoCurrentTime?: React.Dispatch<React.SetStateAction<number>>;
  setPlayerVideoDuration?: React.Dispatch<React.SetStateAction<number>>;
  setPlayerVideoIsPlaying?: React.Dispatch<React.SetStateAction<boolean>>;
  onRefReady?: (ref: {
    seekToSecond: (second: number) => void;
    play: () => void;
    stop: () => void;
  }) => void;

  /**
   * @default false
   */
  fullWidth?: boolean;
  customControlsStyles?: React.CSSProperties;
  onChangeVideoHasError?: (value: boolean) => void;
};

export const VideoPlayer = forwardRef<HTMLDivElement, VideoProps>(
  (
    {
      url,
      playing = true,
      muted,
      activeBox,
      presenceData,
      controls = true,
      volume = 1,
      playbackRate = 1,
      width = '100%',
      height = '100%',
      customControls,
      drawerLogo,
      pendingSeekTime,
      isLoading,
      hasError,
      onRefReady,
      setPendingSeekTime,
      onDrawnRect,
      onPlay,
      onPause,
      onProgress,
      onEnded,
      checkProgress,
      checkSeek,
      onEndPresence,
      setActiveBox,
      setPlayerVideoCurrentTime,
      setPlayerVideoDuration,
      setPlayerVideoIsPlaying,
      customControlsStyles,
      markers,
      onChangeVideoHasError,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { isAutoplayingClips } = useBroadcastScheduleContext();

    // TODO: Only for testing (Error is for apply filters on devTools)
    // console.error('playing for TS safety: ', playing);

    // TODO: Avoiding autoplay while developing...
    // const [state, actions] = useVideoPlayerState(false, volume, width, playbackRate);
    const [state, actions] = useVideoPlayerState(playing, volume, width, playbackRate);

    const {
      isPlaying,
      currentVolume,
      actualWidth,
      currentRate,
      played,
      duration,
      isVideoLoading,
      videoHasError,
      isAudio,
    } = state;

    const {
      setIsPlaying,
      setIsHovered,
      setIsControlsHovered,
      setIsVideoLoading,
      setVideoHasError,
      setIsAudio,
    } = actions;

    const [isPlayerReady, setIsPlayerReady] = useState(false);

    const shouldShowVideoError = useMemo(
      () => hasError || videoHasError,
      [hasError, videoHasError]
    );
    const shouldShowLoader = useMemo(
      () => (isLoading || isVideoLoading) && !shouldShowVideoError,
      [isLoading, isVideoLoading, shouldShowVideoError]
    );

    useEffect(() => {
      onChangeVideoHasError && onChangeVideoHasError(shouldShowVideoError);
    }, [shouldShowVideoError]);

    useEffect(() => {
      if (onRefReady) {
        onRefReady({
          seekToSecond: (second: number) => {
            playerRef.current?.seekTo(second, 'seconds');
          },
          play: () => {
            setIsPlaying(true);
          },
          stop: () => {
            setIsPlaying(false);
          },
        });
      }
    }, [onRefReady]);

    useEffect(() => {
      stopRewindRef.current?.();
      setIsPlayerReady(false);
      if (!url) {
        setIsVideoLoading(false);
        setVideoHasError(true);
        return;
      }
      if (url.match(/\.(mp3|wav|ogg)(\?.*)?$/i) !== null) {
        setIsAudio(true);
      } else {
        setIsAudio(false);
      }
      setVideoHasError(false);
      setIsVideoLoading(true);
    }, [url]);

    const playerRef = useRef<ReactPlayer | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const videoElementRef = useRef<HTMLVideoElement | null>(null);
    const stopRewindRef = useRef<(() => void) | null>(null);

    const handlers = useVideoPlayerHandlers(
      { ...state, videoHasError, isVideoLoading },
      actions,
      playerRef,
      {
        drawerLogo,
        hasError,
        isLoading,
        pendingSeekTime,
        onPlay,
        onPause,
        onProgress,
        onEndPresence,
        onEnded,
        activeBox,
        presenceData,
        checkProgress,
        checkSeek,
        setActiveBox,
        setPlayerVideoCurrentTime,
        setPlayerVideoDuration,
        setPendingSeekTime,
        stopRewind: () => stopRewindRef.current?.(),
      }
    );

    const {
      handlePlayPause,
      handleProgress,
      handleEnded,
      handleDuration,
      handleSeek,
      handleVolumeChange,
      handlePlaybackRateChange,
      handleFullscreen,
      handleReady,
      handleError,
      handleChangeWidth,
    } = handlers;

    const { stopRewind, reverseRate } = useVideoKeyboardHandlers(
      handlers,
      {
        played,
        duration,
        currentVolume,
        isPlaying,
        currentRate,
      },
      containerRef
    );

    stopRewindRef.current = stopRewind;

    const handleControlsPlaybackRateChange = useCallback(
      (value: number) => {
        const wasReversing = reverseRate !== null;
        stopRewind();
        handlePlaybackRateChange(value);
        if (wasReversing) {
          handlers.handlePlay();
        }
      },
      [reverseRate, stopRewind, handlePlaybackRateChange, handlers.handlePlay]
    );

    // The bar must keep working while drawing, so it bypasses the drawing guard of
    // handlePlayPause; that guard stays for clicks on the player itself.
    const handleControlsPlayPause = useCallback(() => {
      stopRewind();
      if (isPlaying) {
        handlers.handlePause();
      } else {
        handlers.handlePlay();
      }
    }, [isPlaying, stopRewind, handlers.handlePause, handlers.handlePlay]);

    useEffect(() => {
      actions.setIsPlaying(isAutoplayingClips);
    }, [isAutoplayingClips]);

    useEffect(() => {
      setPlayerVideoIsPlaying?.(isPlaying);
    }, [isPlaying, setPlayerVideoIsPlaying]);

    const onPauseRef = useRef(onPause);
    onPauseRef.current = onPause;

    useEffect(() => {
      if (drawerLogo) {
        setIsPlaying(false);
        onPauseRef.current?.();
      }
    }, [drawerLogo]);

    return (
      <div
        ref={node => {
          (containerRef.current as HTMLDivElement | null) = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        tabIndex={0}
        onClick={() => {
          handlePlayPause();
          containerRef.current?.focus();
        }}
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        className={clsx(
          'relative flex w-full flex-col justify-center',
          height,
          shouldShowLoader || (shouldShowVideoError && 'bg-[#DDF3FF]')
        )}
      >
        {shouldShowLoader && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-bg-primary-25 px-2 py-4">
            <ScreenProcessingIcon size={IconSizeEnum.CUSTOM} className="h-40 w-40" />
            <LoaderIcon className="animate infinite" />
            <Typography fontSize={FontSizeEnum.Xl} weight={FontWeightEnum.Bold}>
              {t('Cargando...')}
            </Typography>
          </div>
        )}
        {!shouldShowVideoError ? (
          <div className="relative mb-10 flex h-full w-full items-center justify-center">
            {isAudio && (
              <ReactPlayer
                url="/radio.mp4"
                playing={isPlaying}
                muted={true}
                controls={false}
                volume={0}
                playbackRate={currentRate}
                width={shouldShowLoader ? '0px' : actualWidth}
                height={shouldShowLoader ? '0px' : '100%'}
                loop={true}
                style={{ height: `${Number(actualWidth.replace('%', '')) * 1.5}px` }}
              />
            )}
            <ReactPlayer
              className="video__animated__border--wrapper"
              url={url}
              playing={isPlaying}
              muted={muted}
              controls={controls}
              volume={currentVolume}
              playbackRate={currentRate}
              width={shouldShowLoader ? '0px' : actualWidth}
              height={shouldShowLoader ? '0px' : '100%'}
              onPlay={onPlay}
              onPause={onPause}
              onProgress={handleProgress}
              onEnded={handleEnded}
              onDuration={handleDuration}
              onError={handleError}
              progressInterval={100}
              onReady={() => {
                handleReady();
                setIsPlayerReady(true);
              }}
              ref={instance => {
                playerRef.current = instance;
                if (instance) {
                  const internal = instance.getInternalPlayer() as HTMLVideoElement | null;
                  if (internal && internal.tagName === 'VIDEO') {
                    videoElementRef.current = internal;
                  }
                }
              }}
              style={{ position: isAudio ? 'absolute' : 'relative' }}
            />
          </div>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-bg-primary-25 px-2 py-4">
            <ScreenEmptyVideoIcon size={IconSizeEnum.CUSTOM} className="h-40 w-40" />
            <Typography fontSize={FontSizeEnum.Xl} weight={FontWeightEnum.Bold}>
              {t('No hay vídeo disponible')}
            </Typography>
          </div>
        )}

        {customControls && isPlayerReady && (
          <CustomControlsWidget
            containerRef={containerRef}
            isPlaying={isPlaying}
            played={played}
            duration={duration}
            volume={currentVolume}
            playbackRate={currentRate}
            onPlayPause={handleControlsPlayPause}
            onSeek={handleSeek}
            onVolumeChange={handleVolumeChange}
            onPlaybackRateChange={handleControlsPlaybackRateChange}
            reverseRate={reverseRate}
            onFullscreen={handleFullscreen}
            onChangeWidth={handleChangeWidth}
            setIsControlsHovered={setIsControlsHovered}
            styles={customControlsStyles}
            markers={markers}
          />
        )}

        {!isAudio && activeBox && (
          <VideoBoxes
            activeBox={activeBox}
            containerRef={containerRef}
            videoRef={videoElementRef}
          />
        )}

        {!isAudio && drawerLogo && (
          <VideoCanvas
            drawerLogo={drawerLogo}
            onDrawnRect={onDrawnRect}
            containerRef={containerRef}
            videoRef={videoElementRef}
          />
        )}
      </div>
    );
  }
);
